import { useMount, useUpdateEffect } from 'ahooks';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import useContext from '../../../hooks/useContext';
import {
  TPossibleValues,
  previousValidationActions,
} from '../../../store/previousValidationSlice';
import { ICustomValidate, ITypicalEvent, IValueEvent } from './types';
import { ValidationContext } from './ValidationContext';

function isTypical<ValueType>(
  ev: ITypicalEvent<ValueType> | IValueEvent<ValueType>,
): ev is ITypicalEvent<ValueType> {
  return 'target' in ev;
}

/**
 * @see validators
 * @see useValidation
 *
 * Permite el uso de validaciones de modo sencillo a lo largo de la aplicación.
 * La forma de uso para la que fue diseñada consiste en tener un contexto que
 * especifique el formulario al cual pertenecen los campos definidos dentro de
 * cada componente. Esto se logra de forma sencilla mediante el componente
 * ValidationContext.
 *
 * Una vez que está establecido el contexto, se podrá utilizar este hook en
 * cualquier descendiente del mismo, obteniendo un proceso de validación y
 * recolección de datos automático.
 *
 * El hook provee funcionalidad de recolección de datos, parseo de valores
 * antes de hacer submit, validación de errores, provisión de mensajes de error
 * en el lenguaje de la aplicación, foco automático en los campos con error y
 * previa apertura de la pestaña en la que se encuentre el control en caso de
 * que estuviera cerrada.
 *
 * @param name - El nombre que tendrá el control al realizar el submit
 *
 * @param initialValue - El valor inicial que tendrá el control en cuanto a la
 * vista.
 *
 * @param onValueChange - Este callback contempla la necesidad de utilizar el
 * valor en un lugar distinto a donde está siendo seteado, o simplemente si se
 * quiere tener acceso al valor del campo en forma ordenada. Cada vez que se
 * setea un nuevo valor, este callback será llamado con el mismo, además del
 * valor de submit, obtenido mediante el submitValueParser.
 *
 * @param validations - Un objeto del tipo TValidate. Este objeto es el que
 * definirá cuáles son las validaciones que se aplicarán al campo, existe una
 * gran variedad de validaciones posibles. Las más comunes son: required, maxLength,
 * minLength, min, max, validName, alpha, alphanum, entre otras. Ver TValidate para
 * conocer la lista completa de validaciones
 *
 * @param submitValueParser - Es una función que se usará durante todo el proceso
 * de recolección de datos para modificar los valores introducidos por el usuario
 * de forma que se adapten a las necesidades al hacer submit.
 *
 * El proceso en resumen consiste de: declarar un contexto de validación (un
 * formulario), agregar todos los campos que se desee (descendientes del
 * contexto de validación) y finalmente ejecutar la validación antes de hacer
 * submit. Este último paso se realiza mediante el hook useValidation.
 *
 * Existen además de este hook, algunos componentes ya hechos que pueden
 * utilizarse para realizar validación: input, select, textarea y checkbox. Los
 * mismos se encuentran en la misma ruta que el hook. Se puede ver su
 * documentación en el archivo correspondiente a cada uno.
 *
 * @returns Un objeto con la forma { error, onChange, value }
 * @returns error - Es el mensaje de error asociado al campo actual, el mismo va a ser
 * siempre undefined a menos que exista un error asociado al campo, en cuyo caso será
 * un string conteniendo el mensaje de error en el lenguaje de la aplicación
 * @returns onChange - Es el método que se usará para actualizar el valor del campo,
 * el mismo recibe como parámetro o bien { value: ... } o bien { target: { value: ... }}
 * de forma que puede ser usado de foroma personalizada (con la primera forma) o automática
 * asignándolo al onChange de un field (en la segunda forma).
 * @returns value - El valor actual del campo, utilizado para generar componentes
 * controlados mayormente. En caso de que se haya pasado una función en submitValueParser
 * este valor puede diferir del submitValue obtenido con el hook de validación.
 *
 * @see ValidationContext
 * @see TValidate
 * @see useValidation
 * @see validation/Input
 * @see validation/Checkbox
 * @see validation/Select
 * @see validation/Textarea
 *
 * @example
 *
 * # Obteniendo un contexto de validación
 *
 * <ValidationContext form="DatosPersonales">{children}</ValidationContext>
 *
 * @example
 * const { error, onChange, value } = useValidateField<string>({
 *   name: 'name',
 *   validations: { required: true, validName: true, maxLength: 50, minLength:
 *   5 }, initialValue: '',
 *   submitValueParser: (currentName) => `Nombre introducido: ${currentName}`
 * });
 *
 */
export default function useValidateField<
  ValueType extends TPossibleValues,
  SubmitValueType = unknown,
>({
  initialValue: incomingInitialValue,
  name,
  onValueChange,
  validations,
  submitValueParser,
}: ICustomValidate<ValueType, SubmitValueType>) {
  const { form } = useContext(ValidationContext);
  const dispatch = useAppDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValue = React.useMemo(() => incomingInitialValue, []);
  const hasInitialized = React.useRef(false);

  useMount(() => {
    dispatch(
      previousValidationActions.setValidations({
        form,
        name,
        initialValue,
        initialSubmitValue: submitValueParser
          ? submitValueParser(initialValue)
          : initialValue,
        validations,
      }),
    );
  });

  const { value, submitValue } = useAppSelector((global) => {
    const store = global.previousValidationSlice.forms[form];
    return {
      value: store?.values[name],
      submitValue: store?.submitValues[name],
    };
  }, shallowEqual);

  useUpdateEffect(() => {
    if (onValueChange && hasInitialized.current) {
      onValueChange({
        value: value as ValueType,
        submitValue: submitValue as SubmitValueType,
      });
    }
    hasInitialized.current = true;
  }, [value, submitValue]);

  const error = useAppSelector(
    (global) => global.previousValidationSlice.forms[form]?.errors[name],
  );

  const setValue = React.useCallback(
    (newValue: ValueType) => {
      const newSubmitValue = submitValueParser
        ? submitValueParser(newValue)
        : newValue;
      dispatch(
        previousValidationActions.setValue({
          form,
          name,
          value: newValue,
          submitValue: newSubmitValue,
        }),
      );
    },
    [dispatch, form, name, submitValueParser],
  );

  const onChange = React.useCallback(
    (ev: ITypicalEvent<ValueType> | IValueEvent<ValueType>) => {
      const currentValue = isTypical(ev) ? ev.target.value : ev.value;
      setValue(currentValue);
    },
    [setValue],
  );

  return React.useMemo(
    () => ({
      error,
      onChange,
      setValue,
      value: (value ?? initialValue) as ValueType,
    }),
    [error, initialValue, onChange, setValue, value],
  );
}
